import { Controller } from "@hotwired/stimulus"

// Displays a freshly-minted gateway payment method token and counts down its
// short validity window. The token is generated in the browser by the Fluid Pay
// Tokenizer (see fluid_pay_controller) and never touches our server; this
// controller only surfaces it for the operator to copy. Fluid Pay tokens expire
// two minutes after issuance, so the countdown mirrors that.
const TOKEN_TTL_SECONDS = 120

export default class extends Controller {
  static targets = ["countdown", "copyButton", "expired", "panel", "token"]

  disconnect() {
    this.stopCountdown()
  }

  // Once expired the token is useless, so blank it out and clear the copy
  // payload to keep a stale value from being handed off.
  expire() {
    this.stopCountdown()
    this.countdownTarget.textContent = "0:00"
    this.tokenTarget.textContent = "—"
    this.copyButtonTarget.dataset.copy = ""
    this.copyButtonTarget.classList.add("hidden")
    this.expiredTarget.classList.remove("hidden")
  }

  // Background tabs throttle or fully pause setInterval, so re-derive the display
  // from the wall clock the instant the tab is shown again rather than waiting for
  // (or trusting) the next tick.
  onVisibilityChange = () => {
    if (!document.hidden) this.tick()
  }

  renderCountdown() {
    const minutes = Math.floor(this.remaining / 60)
    const seconds = String(this.remaining % 60).padStart(2, "0")
    this.countdownTarget.textContent = `${minutes}:${seconds}`
  }

  show(event) {
    const token = event.detail?.token
    if (!token) return

    this.tokenTarget.textContent = token
    this.copyButtonTarget.dataset.copy = token
    this.expiredTarget.classList.add("hidden")
    this.copyButtonTarget.classList.remove("hidden")
    this.panelTarget.classList.remove("hidden")
    this.panelTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })

    this.startCountdown()
  }

  startCountdown() {
    this.stopCountdown()
    // Track the absolute expiry rather than decrementing a per-tick counter: a
    // background tab throttles or pauses setInterval, which would freeze the timer
    // while hidden and resume where it left off. Deriving the remaining time from a
    // timestamp keeps it honest — a hidden-then-shown tab jumps to the real value.
    this.expiresAt = Date.now() + TOKEN_TTL_SECONDS * 1000
    this.tick()
    this.interval = setInterval(() => this.tick(), 1000)
    document.addEventListener("visibilitychange", this.onVisibilityChange)
  }

  stopCountdown() {
    if (this.interval) clearInterval(this.interval)
    this.interval = null
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
  }

  tick() {
    this.remaining = Math.max(0, Math.ceil((this.expiresAt - Date.now()) / 1000))
    if (this.remaining <= 0) {
      this.expire()
    } else {
      this.renderCountdown()
    }
  }
}
